import { Box, SxProps } from '@mui/material';
import { ORANGE, WHITE } from 'constants/colors';
import { isValidElement, JSX } from 'react';
import {
  LocationType,
  SchedulingLabel,
  TimeWindowResponse
} from 'generated/index';
import NonRecurringIcon from '../NonRecurringIcon/NonRecurringIcon';
import styled from 'styled-components';
import { StyledExtraSmallIcon } from 'components/shared/Icon/Icon.styles';

type SignalRowProps = {
  schedulingLabel?: SchedulingLabel;
  addressNotFound?: boolean;
  noteInternal?: string;
  excludedEmployeeClientCombination?: boolean;
  hasOverlap?: boolean;
  isAbsent?: boolean;
  isInSyncWithEcd?: boolean;
  isRecurring?: boolean;
  locationType?: LocationType;
  overQualified?: boolean;
  selected?: boolean;
  signalThreshold: number;
  startsTooEarly?: boolean;
  startsTooLate?: boolean;
  timeWindow?: TimeWindowResponse;
  underQualified?: boolean;

  maxWidth?: number;
  sx?: SxProps;
};

const IconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
  &:first-of-type {
    margin-left: -3px;
  }
`;

const ActivitySignalRow = ({
  children,
  threshold,
  maxWidth = 0
}: {
  children: unknown[];
  threshold: number;
  maxWidth?: number;
}) => {
  const ICON_WIDTH = 20;
  const hasMaxWidth = maxWidth && maxWidth > 0;

  // Filter out any non-React elements first (like booleans due to conditional rendering)
  const elements: JSX.Element[] = children.filter((child: unknown) =>
    isValidElement(child)
  ) as JSX.Element[];

  const maxWidthThreshold = Math.floor(maxWidth / ICON_WIDTH);
  const thresholdExceeded =
    elements.length > (hasMaxWidth ? maxWidthThreshold : threshold);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="18px"
    >
      {elements.filter((_: JSX.Element, i: number) => {
        if (hasMaxWidth) return i <= maxWidthThreshold - 1;
        return i <= threshold - 1;
      })}
      {thresholdExceeded && (
        <Box
          display="flex"
          alignItems="center"
          sx={{
            height: '15px',
            width: '15px',
            paddingLeft: '2px',
            borderRadius: '50%',
            boxSizing: 'border-box',
            backgroundColor: ORANGE,
            color: WHITE,
            fontSize: '9px',
            // Not clickable — inherit the card's cursor so hovering it does not
            // flicker between the card's grab cursor and a pointer.
            cursor: 'inherit'
          }}
        >
          +{elements.length - (hasMaxWidth ? maxWidthThreshold : threshold)}
        </Box>
      )}
    </Box>
  );
};

const StyledSignalRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  boxSizing: 'border-box',
  padding: '2px 0 2px 8px',
  width: '80px'
});

const SignalRow = ({
  schedulingLabel,
  addressNotFound,
  noteInternal,
  excludedEmployeeClientCombination,
  hasOverlap,
  isInSyncWithEcd,
  isRecurring,
  locationType,
  overQualified,
  signalThreshold,
  startsTooEarly,
  startsTooLate,
  timeWindow,
  underQualified,
  maxWidth = 0
}: SignalRowProps) => {
  return (
    <StyledSignalRow>
      <ActivitySignalRow threshold={signalThreshold} maxWidth={maxWidth}>
        {schedulingLabel === 'VV' && isRecurring && (
          <IconContainer>
            <StyledExtraSmallIcon type="arrows-repeat" />
          </IconContainer>
        )}
        {schedulingLabel === 'VV' && !isRecurring && <NonRecurringIcon />}
        {startsTooLate && (
          <IconContainer>
            <StyledExtraSmallIcon type="chevron-up" />
          </IconContainer>
        )}
        {startsTooEarly && (
          <IconContainer>
            <StyledExtraSmallIcon type="chevron-down" />
          </IconContainer>
        )}
        {timeWindow?.isUrgent && (
          <IconContainer>
            <StyledExtraSmallIcon type="lock" color="error" />
          </IconContainer>
        )}
        {locationType === 'TELECARE' && (
          <IconContainer>
            <StyledExtraSmallIcon type="video" />
          </IconContainer>
        )}
        {noteInternal && (
          <IconContainer>
            <StyledExtraSmallIcon type="note" />
          </IconContainer>
        )}
        {excludedEmployeeClientCombination && (
          <IconContainer>
            <StyledExtraSmallIcon type="user-xmark" />
          </IconContainer>
        )}
        {underQualified && (
          <IconContainer>
            <StyledExtraSmallIcon type="user-minus" />
          </IconContainer>
        )}
        {overQualified && (
          <IconContainer>
            <StyledExtraSmallIcon type="user-plus" />
          </IconContainer>
        )}
        {!isInSyncWithEcd && (
          <IconContainer>
            <StyledExtraSmallIcon type="rotate-exclamation" />
          </IconContainer>
        )}
        {addressNotFound && (
          <IconContainer>
            <StyledExtraSmallIcon type="location-dot-slash" />
          </IconContainer>
        )}
        {hasOverlap && (
          <IconContainer>
            <StyledExtraSmallIcon type="triangle-exclamation" />
          </IconContainer>
        )}
      </ActivitySignalRow>
    </StyledSignalRow>
  );
};

export default SignalRow;
